import assert from 'assert'
import type { AccountInfo } from '@azure/msal-common'
import { Exclude, instanceToPlain } from 'class-transformer'
import type {
	IContact,
	IRequestsRepository,
	IUser,
	IActivityLogRepository
} from '@services/types'
import { IUserDTO } from '@services/dtos/users.dto'
import UsersRepository from '@services/repositories/users.repository'
import { Contact } from '@services/models/contacts.model'
import { IContactDTO } from '@services/dtos/contacts.dto'
import { Request } from '@services/models/requests.model'
import RequestsRepository from '@services/repositories/requests.repository'
import { IRequestDTO } from '@services/dtos/requests.dto'
import {
	ActivityLogRepository,
	RepereIntegrationRepository
} from '@services/repositories'
import { IActivityLogItemDTO } from '@services/dtos/_miscellaneous.dto'
import { ActivityLogItem } from '@services/models/_miscellaneous.model'
import { RequestPartialsEnum } from '@services/constants'

// TODO: add namespace accross multiple files namespace Models {
export class User implements IUser {
	// TODO: write a dto for the AccountInfo Object
	@Exclude()
	identity: AccountInfo

	@Exclude()
	profile: Contact | null = null

	@Exclude()
	private _hasProfile: boolean = false

	@Exclude()
	private _requestsRepository?: IRequestsRepository

	@Exclude()
	private _activityLogRepository?: IActivityLogRepository

	@Exclude()
	private _myRequests?: Request[]

	@Exclude()
	private _myRequestsMap: Map<string, Request>

	@Exclude()
	private _myRequest?: Request

	constructor(identity: AccountInfo, profile?: Contact | null) {
		if (!profile || !User.isValideProfile(profile)) {
			throw new Error(
				'Cannot instanciate a User without a valid profile; call the build function instead'
			)
		}

		this._myRequestsMap = new Map<string, Request>()
		this._requestsRepository = new RequestsRepository()
		this._activityLogRepository = new ActivityLogRepository()
		this.identity = identity
		this.profile = profile
		this._hasProfile = true
	}

	@Exclude()
	static buildAsync = async (
		identity: AccountInfo,
		profile?: Contact | null
	): Promise<User> => {
		let user: User
		const repository = new UsersRepository()

		if (!profile || !User.isValideProfile(profile)) {
			const contactDTO = await repository.getProfile(identity.localAccountId)

			user = new User(identity, new Contact(contactDTO))
		} else {
			user = new User(identity, profile)
		}

		/* This is obsolete and slows down load times */
		//await user.fetchRequests()

		return user
	}

	// TODO: to update with class-validator and json-rule-engines
	@Exclude()
	static isValideProfile = (profile: IContact): boolean => {
		// validate profile
		return true
	}

	@Exclude()
	get hasProfile(): boolean {
		return this._hasProfile
	}

	/**
	 * Summary: Fetches all the requests created by the current user.
	 *
	 * Description: Fetches all the requests sharing the field customer_id.
	 *
	 * @since      0.3.9
	 * @since      0.4.4                  added param withPartials.
	 * @param      {boolean} withPartials also fetch the partials of the requests.
	 * @access     public
	 *
	 */
	@Exclude()
	public async fetchRequests(
		withPartials: boolean = false,
		page_number?: number,
		processing_state?: number,
		filter_by?: number,
		order_by?: number,
		exclude_contact_property_incidents: boolean = false
	): Promise<any> {
		assert(
			this.hasProfile && this.profile?.id,
			'Can not fetch requests for a user not having a profile'
		)

		const searchResult = await this._requestsRepository?.searchRequestsBy(
			'customerId',
			this.profile.id,
			true,
			page_number,
			5,
			processing_state,
			filter_by,
			order_by,
			exclude_contact_property_incidents
		)
		const currentPage = searchResult?.page_number
		const totalPages = Math.ceil(searchResult?.total_count / 5)

		const requestsDTOList: IRequestDTO[] | undefined = page_number
			? searchResult?.incidents
			: searchResult

		const requests: Request[] | undefined = requestsDTOList?.map(
			(requestDTO) => new Request(this, requestDTO)
		)

		if (withPartials && requests) {
			for (const request of requests) {
				await Request.updatePartials(request)
			}
		}

		/*if (partial && requests) {
			for (const request of requests) {
				const requestCalls: Array<Promise<void>> = []

				for (const element of partial) {
					requestCalls.push(Request.getPartial(request, element))
				}
				await Promise.allSettled(requestCalls)
			}
		}*/

		this._myRequests = requests

		requests?.forEach((request) => {
			if (request.id) {
				this._myRequestsMap.set(request.id, request)
			}
		})

		if (page_number) {
			return { requests, currentPage, totalPages }
		} else {
			return requests ?? ([] as Request[])
		}
	}

	@Exclude()
	public async fetchRequest(requestId: string): Promise<Request> {
		assert(
			this.hasProfile && this.profile?.id,
			'Can not fetch requests for a user not having a profile'
		)

		const requestDTO = await this._requestsRepository?.getRequest(requestId)

		const request = new Request(this, requestDTO)

		this._myRequest = request

		return request as Request
	}

	@Exclude()
	public async fetchPartials(
		request?: Request,
		partial?: RequestPartialsEnum[]
	): Promise<Request> {
		assert(
			this.hasProfile && this.profile?.id,
			'Can not fetch requests for a user not having a profile'
		)

		const requestCalls: Array<Promise<void>> = []

		for (const element of partial!) {
			requestCalls.push(Request.getPartial(request!, element))
		}

		await Promise.allSettled(requestCalls)

		return request as Request
	}

	@Exclude()
	public async fetchRecentActivities(): Promise<ActivityLogItem[]> {
		assert(
			this.hasProfile && this.profile?.id,
			'Can not fetch recent activities for a user not having a profile'
		)

		const recentActivitiesDTOList: IActivityLogItemDTO[] | undefined =
			await this._activityLogRepository?.searchActivitiesByResourceIdAsync(
				'customer_id',
				this.profile.id
			)

		const recentActivities: ActivityLogItem[] | undefined =
			recentActivitiesDTOList?.map(
				(recentActivitiesDTO) => new ActivityLogItem(recentActivitiesDTO)
			)

		return recentActivities ?? ([] as ActivityLogItem[])
	}

	@Exclude()
	get requests(): Request[] {
		return this._myRequests ?? ([] as Request[])
	}

	@Exclude()
	public getMyRequest(): Request | undefined {
		return this._myRequest
	}

	@Exclude()
	public getRequest(requestId: string): Request | undefined {
		return this._myRequestsMap.get(requestId)
	}

	@Exclude()
	public setRequest(request: Request): void {
		if (request.id) {
			this._myRequestsMap.set(request.id, request)
		}
	}

	@Exclude()
	public hasRequest(requestId: string): boolean {
		return this._myRequestsMap.has(requestId)
	}

	@Exclude()
	static parse = async (userDTO: IUserDTO): Promise<User> => {
		const user = await User.buildAsync(
			userDTO.identity,
			// userDTO.profile ? Contact.parse(userDTO.profile) : null
			null
		)

		return user
	}

	@Exclude()
	static serialize = (user: User): IUserDTO => {
		const profile: IContactDTO = user.profile
			? Contact.serialize(user.profile)
			: {}
		const identity = instanceToPlain(user.identity) as AccountInfo

		return { profile, identity }
	}

	@Exclude()
	static forceSyncRequests = async (userId: string): Promise<boolean> => {
		const repository = new RepereIntegrationRepository()

		const result = await repository.forceSyncUserRequests({ userId })

		if (!result) {
			return false
		}

		return true
	}
}
